/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */
import React from "react";

import Dialog from "@mui/material/Dialog";
import { useTheme } from "@mui/material/styles";

import { dvhFallback } from "../../theme";
import { layerInfoHeadingValuesSx, layerInfoTextSx } from "./layerInfoStyles";

// Selector that targets h1-h6 only inside DialogContent (avoids restyling
// DialogTitle, which MUI renders as <h2>).
export const DIALOG_CONTENT_HEADING_SELECTOR =
  "&& .MuiDialogContent-root h1, && .MuiDialogContent-root h2, && .MuiDialogContent-root h3, && .MuiDialogContent-root h4, && .MuiDialogContent-root h5, && .MuiDialogContent-root h6";

interface NarrowDialogFallbackProps {
  open: boolean;
  onClose: (event?: any, reason?: string) => void;
  children: React.ReactNode;
}

export function NarrowDialogFallback({
  open,
  onClose,
  children,
}: NarrowDialogFallbackProps) {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "90vw",
          maxWidth: "600px",
          ...dvhFallback("maxHeight", 40),
          ...layerInfoTextSx,
          fontSize: "0.9rem",
          "&& p, && li, && a, && span, && code, && .MuiTypography-root, && .MuiAlert-message":
            {
              fontSize: "0.9rem",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            },
          "&& .MuiDialogTitle-root": {
            fontSize: "1.2rem !important",
            fontWeight: 400,
            color: theme.palette.text.secondary,
            paddingRight: theme.spacing(6),
          },
          "&& .MuiDialogContent-root": {
            paddingTop: 0,
          },
          [DIALOG_CONTENT_HEADING_SELECTOR]: layerInfoHeadingValuesSx(theme),
        },
      }}
    >
      {children}
    </Dialog>
  );
}
